<template>
  <div class="p-8 bg-[#F4F7F9] min-h-screen font-sans text-[#222]">

    <header class="flex flex-col md:flex-row justify-between items-end mb-10 gap-6">
      <div>
        <div class="flex items-center gap-3 mb-2">
          <h1 class="text-4xl font-black tracking-tighter uppercase italic">Incidents Hub</h1>
          <span class="px-4 py-1 bg-red-600 text-white rounded-full text-[11px] font-black animate-pulse">
            {{ machinesCritiques.length }} CRITIQUES
          </span>
        </div>
        <p class="text-gray-500 font-bold uppercase text-[10px] tracking-[0.2em]">Flux centralisé des terminaux défectueux uniquement</p>
      </div>

      <div class="flex gap-4">
        <div class="bg-white p-4 rounded-[1.5rem] shadow-sm border border-gray-100 flex items-center gap-4">
          <div class="flex -space-x-2">
            <div class="w-8 h-8 rounded-full bg-red-500 border-2 border-white"></div>
            <div class="w-8 h-8 rounded-full bg-orange-400 border-2 border-white"></div>
            <div class="w-8 h-8 rounded-full bg-blue-400 border-2 border-white"></div>
          </div>
          <span class="text-[10px] font-black uppercase text-gray-400">{{ machinesEnErreur.length }} Machines à traiter</span>
        </div>
      </div>
    </header>

    <div v-if="loading" class="flex items-center justify-center py-40">
      <div class="text-center">
        <div class="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-gray-400 font-bold">Chargement des dispositifs...</p>
      </div>
    </div>

    <div v-else class="grid xl:grid-cols-3 gap-6">

      <div v-for="machine in machinesEnErreur" :key="machine._id"
           class="group bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm hover:shadow-2xl transition-all relative overflow-hidden">

        <div :class="severityClass(machine.health.status)" class="absolute left-0 top-0 bottom-0 w-2"></div>

        <div class="flex justify-between items-start mb-6">
          <div>
            <span class="text-[9px] font-black uppercase px-2 py-1 rounded bg-gray-100 text-gray-500 mb-2 inline-block">
              {{ machine.type }} - {{ machine.identifier }}
            </span>
            <h3 class="text-xl font-black leading-none">{{ machine.name || 'Sans nom' }}</h3>
            <p class="text-[10px] font-bold text-dimotec-orange mt-1 uppercase tracking-widest">{{ getAgencyName(machine.agencyId) }}</p>
          </div>
          <div :class="statusBg(machine.health.status)" class="p-3 rounded-2xl">
            <svg v-if="machine.health.status === 'critical'" class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <svg v-else class="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
        </div>

        <div class="bg-gray-50 rounded-2xl p-4 mb-8">
          <div class="flex justify-between items-center mb-2">
            <span class="text-[9px] font-black text-gray-400 uppercase">Défauts détectés</span>
            <span class="text-[10px] font-mono text-gray-400">{{ getTimeSince(machine.health.lastCheck) }}</span>
          </div>
          <div v-if="machine.health.issues && machine.health.issues.length > 0" class="space-y-2">
            <div v-for="(issue, idx) in machine.health.issues.slice(0, 2)" :key="idx">
              <p class="text-sm font-bold text-gray-700">{{ issue.type || 'Erreur système' }}</p>
              <p class="text-[10px] font-mono text-red-500 bg-red-50 inline-block px-2 py-0.5 rounded">
                {{ issue.severity || 'UNKNOWN' }}
              </p>
            </div>
            <p v-if="machine.health.issues.length > 2" class="text-[9px] text-gray-400 font-bold">
              + {{ machine.health.issues.length - 2 }} autres problèmes
            </p>
          </div>
          <p v-else class="text-sm font-bold text-gray-700">Aucun détail disponible</p>
        </div>

        <div class="flex gap-4 mb-8 border-b border-gray-50 pb-6">
          <div class="flex-1 text-center border-r border-gray-50">
            <p class="text-[8px] font-black text-gray-400 uppercase">État</p>
            <p class="text-xs font-black" :class="machine.online ? 'text-green-500' : 'text-red-500'">
              {{ machine.online ? 'EN LIGNE' : 'HORS LIGNE' }}
            </p>
          </div>
          <div class="flex-1 text-center">
            <p class="text-[8px] font-black text-gray-400 uppercase">Temp</p>
            <p class="text-xs font-black text-gray-900">
              {{ machine.metrics?.temperature || 'N/A' }}{{ machine.metrics?.temperature ? '°C' : '' }}
            </p>
          </div>
        </div>

        <button @click="goToIntervention(machine._id)"
                class="w-full py-4 bg-gray-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 group-hover:bg-blue-600 transition-all shadow-xl shadow-gray-200 group-hover:shadow-blue-200">
          Accéder au contrôle
          <svg class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
        </button>
      </div>

    </div>

    <div v-if="!loading && machinesEnErreur.length === 0" class="flex flex-col items-center justify-center py-40">
      <div class="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <svg class="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
      </div>
      <h3 class="text-2xl font-black text-gray-900">Tout est opérationnel</h3>
      <p class="text-gray-400 font-medium">Aucun incident détecté sur les terminaux.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { io } from 'socket.io-client';
import { toast } from 'vue3-toastify';

const router = useRouter();
const loading = ref(true);
const machinesEnErreur = ref([]);
const agencies = ref({});
const socket = ref(null);

const machinesCritiques = computed(() => {
  return machinesEnErreur.value.filter(m => m.health?.status === 'critical');
});

const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}`
  };
};

const fetchDevicesWithErrors = async () => {
  try {
    loading.value = true;

    // Récupérer tous les devices
    const res = await fetch('https://api.datafuse.fr/api/super-admin/devices', {
      headers: getHeaders()
    });

    if (!res.ok) {
      throw new Error('Erreur lors du chargement des devices');
    }

    const allDevices = await res.json();

    // Filtrer uniquement ceux avec des erreurs non résolues
    machinesEnErreur.value = allDevices.filter(device => {
      const hasIssues = device.health?.issues && device.health.issues.length > 0;
      const isCriticalOrWarning = ['critical', 'warning'].includes(device.health?.status);
      return hasIssues && isCriticalOrWarning;
    });

    console.log(`✅ ${machinesEnErreur.value.length} machines avec erreurs trouvées`);

    // Charger les noms des agences
    await fetchAgencies();

  } catch (err) {
    console.error('Erreur:', err);
    toast.error('Erreur lors du chargement des dispositifs');
  } finally {
    loading.value = false;
  }
};

const fetchAgencies = async () => {
  try {
    // Récupérer toutes les agences
    const res = await fetch('https://api.datafuse.fr/api/super-admin/agencies', {
      headers: getHeaders()
    });

    if (res.ok) {
      const agenciesList = await res.json();
      agencies.value = agenciesList.reduce((acc, agency) => {
        acc[agency._id] = agency.name;
        return acc;
      }, {});
    }
  } catch (err) {
    console.error('Erreur chargement agences:', err);
  }
};

const getAgencyName = (agencyId) => {
  return agencies.value[agencyId] || 'Agence inconnue';
};

const getTimeSince = (timestamp) => {
  if (!timestamp) return 'N/A';

  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}j`;
};

const severityClass = (status) => {
  if (status === 'critical') return 'bg-red-600';
  if (status === 'warning') return 'bg-orange-400';
  return 'bg-gray-400';
};

const statusBg = (status) => {
  if (status === 'critical') return 'bg-red-50';
  if (status === 'warning') return 'bg-orange-50';
  return 'bg-gray-50';
};

const goToIntervention = (deviceId) => {
  router.push(`/configuration/${deviceId}`);
};

const initWebSocket = () => {
  const token = localStorage.getItem('superAdminToken');
  if (!token) return;

  socket.value = io('https://api.datafuse.fr', {
    query: { token },
    transports: ['websocket', 'polling']
  });

  socket.value.on('connect', () => {
    console.log('✅ WebSocket connecté pour maintenance');
  });

  // Écouter les alertes critiques
  socket.value.on('device_critical_alert', (data) => {
    console.log('🚨 Alerte critique:', data);
    toast.error(`Alerte: ${data.deviceName} - ${data.issue}`);
    fetchDevicesWithErrors(); // Recharger la liste
  });

  // Écouter les mises à jour de device
  socket.value.on('device_logs', () => {
    fetchDevicesWithErrors(); // Recharger quand des logs arrivent
  });
};

onMounted(() => {
  fetchDevicesWithErrors();
  initWebSocket();
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.disconnect();
  }
});
</script>
